import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Printer } from "lucide-react";
import { Logo } from "../components/Logo";
import { SiteFooter } from "../components/SiteFooter";
import {
  TERMS_META,
  TERMS_INTRO,
  TERMS_SERVICES,
  TERMS_SECTIONS,
} from "../lib/terms";

const renderItem = (item, idx) => {
  if (typeof item === "string") {
    return (
      <p key={idx} className="text-[#3f4b52] leading-relaxed text-[15px]">
        {item}
      </p>
    );
  }
  if (item.type === "list") {
    return (
      <ul key={idx} className="list-disc pl-6 space-y-1 text-[#3f4b52] text-[15px]">
        {item.items.map((li, i) => (
          <li key={i}>{li}</li>
        ))}
      </ul>
    );
  }
  return null;
};

export const TermsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#f5f6f6] text-[#111619]" data-testid="terms-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-[#cfd7db]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" data-testid="terms-logo-link">
            <Logo variant="group" size="md" />
          </Link>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => window.print()}
              className="hidden sm:inline-flex items-center gap-2 font-display uppercase tracking-widest text-xs text-[#3f4b52] hover:text-[#f28a16] transition"
              data-testid="terms-print"
            >
              <Printer size={14} /> Print
            </button>
            <Link
              to="/"
              className="font-display uppercase tracking-widest text-xs text-[#3f4b52] hover:text-[#f28a16] transition flex items-center gap-2"
              data-testid="terms-back-home"
            >
              <ArrowLeft size={14} /> Back
            </Link>
          </div>
        </div>
      </header>

      {/* Title strip */}
      <section className="hero-gradient">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 sm:py-20">
          <div className="inline-flex items-center gap-2 border border-white/25 bg-black/30 backdrop-blur-sm px-3 py-1 rounded-sm mb-5">
            <FileText size={12} className="text-[#ffc438]" />
            <span className="font-display uppercase tracking-widest text-[11px] text-white/90">
              Legal · {TERMS_META.brand}
            </span>
          </div>
          <h1 className="font-display font-black uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl text-white">
            Terms <span className="text-[#ffc438]">&amp;</span> Conditions
          </h1>
          <p className="mt-5 text-white/85 max-w-xl">
            Effective Date:{" "}
            <span className="text-[#ffc438] font-semibold">{TERMS_META.effectiveDate}</span>
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-14 sm:py-20">
        <p className="text-[#3f4b52] leading-relaxed text-[15px]">{TERMS_INTRO}</p>

        <div className="mt-8 bg-white border border-[#cfd7db] rounded-md p-6 sm:p-8">
          <div className="font-display uppercase tracking-widest text-xs text-[#f28a16] mb-3">
            Services Provided
          </div>
          <p className="text-[#3f4b52] text-[15px] mb-3">
            Diesel &amp; Dust Group provides services including but not limited to:
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 list-disc pl-5 text-[#3f4b52] text-[15px]">
            {TERMS_SERVICES.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </div>

        <div className="mt-12 space-y-10">
          {TERMS_SECTIONS.map((section) => (
            <section
              key={section.id}
              id={section.id}
              data-testid={`terms-section-${section.id}`}
              className="scroll-mt-24"
            >
              <h2 className="font-display font-black uppercase text-xl sm:text-2xl text-[#111619] mb-4 border-l-4 border-[#f28a16] pl-3">
                {section.title}
              </h2>
              <div className="space-y-3">
                {section.items.map((it, i) => renderItem(it, i))}
              </div>
            </section>
          ))}
        </div>

        {/* Closing */}
        <div className="mt-16 border-t border-[#cfd7db] pt-8 text-center">
          <div className="font-display font-black uppercase text-2xl text-[#111619]">
            {TERMS_META.brand}
          </div>
          <p className="text-sm text-[#3f4b52] mt-2">{TERMS_META.tagline}</p>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default TermsPage;
